import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Power, RefreshCw, XCircle } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import MenuGuideButton from '../components/MenuGuideButton'
import { api, getApiErrorMessage } from '../lib/api'
import { formatDateTimeMinuteKst } from '../lib/date'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import OpsFontManagementPage from './OpsFontManagementPage'
import BannersPage from './Banners'
import ExternalProgramVersionsPage from './ExternalProgramVersions'
import OperatorAlertManagementPage from './OperatorAlertManagement'
import OperatorPushNotificationsPage from './OperatorPushNotifications'

const TABS = [
  { id: 'auto-batch', label: '자동배치 관리', path: '/ops/other-management' },
  { id: 'fonts', label: '폰트관리', path: '/ops/other-management/fonts' },
  { id: 'banners', label: '배너관리', path: '/ops/other-management/banners' },
  { id: 'alerts', label: '알림관리', path: '/ops/other-management/alerts' },
  { id: 'push-notifications', label: '앱/웹 알림', path: '/ops/other-management/push-notifications' },
  { id: 'versions', label: '버전관리', path: '/ops/other-management/versions' },
]
const DEFAULT_TAB = TABS[0].id

const FALLBACK_LABELS = {
  MESSAGE_TEMPLATE_DAILY_GENERATION: '메시지 템플릿 자동 생성',
  SALES_IMAGE_AUTO_GENERATION: '발송용 이미지 자동 후보 생성',
  INSURANCE_NEWS_RSS_INGEST: '보험뉴스 RSS 자동 수집',
  INSURANCE_NEWS_DAILY_DIGEST_CURATION: '보험뉴스 일일 다이제스트 자동 큐레이션',
  CUSTOMER_INQUIRY_OPERATOR_REMINDER: '고객문의 운영자 알림',
}

function unwrapData(res) {
  const payload = res?.data
  if (payload?.data !== undefined) return payload.data
  return payload ?? null
}

function displayName(row) {
  return row?.displayName || FALLBACK_LABELS[row?.key] || '자동배치'
}

function formatDate(value) {
  if (!value) return '-'
  return formatDateTimeMinuteKst(value)
}

function AutoBatchToggle({ checked, disabled, label, onChange }) {
  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={onChange}
        className={[
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-brand-sky focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
          checked ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-slate-200',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block size-5 rounded-full bg-white shadow transition',
            checked ? 'translate-x-6' : 'translate-x-1',
          ].join(' ')}
        />
      </button>
      <span className={`min-w-8 text-sm font-semibold ${checked ? 'text-emerald-700' : 'text-slate-500'}`}>
        {checked ? 'ON' : 'OFF'}
      </span>
    </div>
  )
}

export default function OpsOtherManagementPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const tabFromLocation = useMemo(() => {
    const requested = location.pathname.split('/').filter(Boolean)[2]
    return TABS.some((item) => item.id === requested) ? requested : DEFAULT_TAB
  }, [location.pathname])
  const [activeTab, setActiveTab] = useState(tabFromLocation)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyKey, setBusyKey] = useState('')
  const [err, setErr] = useState('')
  const [message, setMessage] = useState('')

  const stats = useMemo(() => {
    const enabled = rows.filter((row) => row.enabled).length
    return { total: rows.length, enabled, disabled: Math.max(0, rows.length - enabled) }
  }, [rows])

  const load = useCallback(async () => {
    setErr('')
    setMessage('')
    setLoading(true)
    try {
      const res = await api.get('/ops/auto-batch-settings')
      const data = unwrapData(res)
      setRows(Array.isArray(data) ? data : [])
    } catch (error) {
      setErr(getApiErrorMessage(error, '자동배치 설정을 불러오지 못했습니다.'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    setActiveTab(tabFromLocation)
  }, [tabFromLocation])

  useEffect(() => {
    if (activeTab === 'auto-batch') {
      void load()
    }
  }, [activeTab, load])

  const selectTab = (tabId) => {
    setActiveTab(tabId)
    const target = TABS.find((tab) => tab.id === tabId)
    navigate(target?.path || TABS[0].path)
  }

  const updateEnabled = async (row) => {
    if (!row?.key) return
    const nextEnabled = !row.enabled
    setErr('')
    setMessage('')
    setBusyKey(row.key)
    try {
      const res = await api.patch(`/ops/auto-batch-settings/${encodeURIComponent(row.key)}`, {
        enabled: nextEnabled,
      })
      const updated = unwrapData(res)
      const patch = updated && typeof updated === 'object' ? updated : { enabled: nextEnabled }
      setRows((prev) => prev.map((item) => (item.key === row.key ? { ...item, ...patch } : item)))
      setMessage(`${displayName(row)} ${nextEnabled ? 'ON' : 'OFF'} 처리했습니다.`)
    } catch (error) {
      setErr(getApiErrorMessage(error, '자동배치 설정을 저장하지 못했습니다.'))
    } finally {
      setBusyKey('')
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500">운영자 · 기타관리</div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">기타관리</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MenuGuideButton menuKey="MENU_OPS_OTHER" />
          {activeTab === 'auto-batch' ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              onClick={load}
              disabled={Boolean(busyKey)}
            >
              <RefreshCw className="size-4" />
              새로고침
            </button>
          ) : null}
        </div>
      </div>

      <div className="mb-5 border-b border-slate-200">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="기타관리 탭">
          {TABS.map((tab) => {
            const active = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={[
                  'border-b-2 px-4 py-2 text-sm font-semibold',
                  active
                    ? 'border-brand-sky text-brand-navy'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800',
                ].join(' ')}
                onClick={() => selectTab(tab.id)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {activeTab === 'auto-batch' && err ? <InlineError message={err} className="mb-4 rounded border border-red-100 bg-red-50" /> : null}
      {activeTab === 'auto-batch' && message ? <div className="mb-4 text-sm font-medium text-emerald-700">{message}</div> : null}

      {activeTab === 'auto-batch' ? (
        loading ? (
          <Loading />
        ) : (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded border border-slate-200 bg-white p-4">
              <div className="text-xs font-medium text-slate-500">전체</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{stats.total}</div>
            </div>
            <div className="rounded border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs font-medium text-emerald-700">ON</div>
              <div className="mt-1 text-2xl font-semibold text-emerald-800">{stats.enabled}</div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-medium text-slate-500">OFF</div>
              <div className="mt-1 text-2xl font-semibold text-slate-800">{stats.disabled}</div>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            {rows.map((row) => {
              const enabled = Boolean(row.enabled)
              const busy = busyKey === row.key
              return (
                <section key={row.key} className="rounded border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {enabled ? (
                          <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
                        ) : (
                          <XCircle className="size-4 shrink-0 text-slate-400" />
                        )}
                        <h2 className="break-keep text-base font-semibold text-slate-900">{displayName(row)}</h2>
                      </div>
                      {row.description ? (
                        <p className="mt-2 text-sm leading-6 text-slate-600">{row.description}</p>
                      ) : null}
                    </div>
                    <AutoBatchToggle
                      checked={enabled}
                      disabled={Boolean(busyKey)}
                      label={`${displayName(row)} ${enabled ? '끄기' : '켜기'}`}
                      onChange={() => updateEnabled(row)}
                    />
                  </div>

                  <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                    <div className="rounded bg-slate-50 px-3 py-2">
                      <div className="text-xs font-medium text-slate-500">기본값</div>
                      <div className="mt-1 font-semibold text-slate-800">{row.defaultEnabled ? 'ON' : 'OFF'}</div>
                    </div>
                    <div className="rounded bg-slate-50 px-3 py-2">
                      <div className="text-xs font-medium text-slate-500">수정자</div>
                      <div className="mt-1 truncate font-medium text-slate-800">{row.updatedBy || '-'}</div>
                    </div>
                    <div className="rounded bg-slate-50 px-3 py-2 sm:col-span-2">
                      <div className="text-xs font-medium text-slate-500">수정일</div>
                      <div className="mt-1 font-medium text-slate-800">{formatDate(row.updatedAt)}</div>
                    </div>
                  </div>

                  {busy ? (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                      <Power className="size-3.5" />
                      저장 중
                    </div>
                  ) : null}
                </section>
              )
            })}
          </div>
        </>
        )
      ) : null}
      {activeTab === 'fonts' ? <OpsFontManagementPage embedded /> : null}
      {activeTab === 'banners' ? <BannersPage /> : null}
      {activeTab === 'alerts' ? <OperatorAlertManagementPage /> : null}
      {activeTab === 'push-notifications' ? <OperatorPushNotificationsPage /> : null}
      {activeTab === 'versions' ? <ExternalProgramVersionsPage /> : null}
    </div>
  )
}
